import React, { useState } from 'react';
import { Layout, Menu, Button, Dropdown, Avatar, Breadcrumb, Input, Badge, Space, Modal, Form, message, Drawer } from 'antd';
import {
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    DashboardOutlined,
    UserOutlined,
    AppstoreOutlined,
    ShopOutlined,
    RocketOutlined,
    LogoutOutlined,
    BellOutlined,
    SearchOutlined,
    FileTextOutlined,
    GlobalOutlined,
    BarcodeOutlined,
    TeamOutlined,
    HomeOutlined,
    PlusOutlined,
    UnorderedListOutlined,
    NodeIndexOutlined,
    ExportOutlined,
    ImportOutlined,
    AuditOutlined,
    ShoppingCartOutlined,
    ThunderboltOutlined,
    SafetyCertificateOutlined,
    LockOutlined,
    FileSearchOutlined,
    MenuOutlined,
    BulbOutlined,
    BulbFilled,
    CrownOutlined,
    SettingOutlined,
} from '@ant-design/icons';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from "@/store/auth-store";
import { useThemeStore } from "@/store/theme-store";
import { CommandPalette } from './CommandPalette';
import { StockTakeLockBanner } from './StockTakeLockBanner';
import { WarehouseSwitcher } from './WarehouseSwitcher';
import { useTranslation } from 'react-i18next';
import { usePermission } from '@/features/auth/hooks/usePermission';
import { useTierCheck } from '@/features/subscription/hooks/useCurrentTenant';
import { authApi } from '@/features/auth/api/auth-api';
import { useIsMobile } from '@/hooks/useIsMobile';
import i18n from '../../i18n';
import type { ItemType } from 'antd/es/menu/interface';

const { Header, Sider, Content } = Layout;

export const AppLayout: React.FC = () => {
    const { t } = useTranslation();
    const isMobile = useIsMobile();
    const { darkMode, toggleDarkMode } = useThemeStore();
    const [collapsed, setCollapsed] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout } = useAuthStore();
    const { hasPermission, hasAnyPermission, isPlatformAdmin } = usePermission();
    // Tier gating for the menu — we still show Taobao / Flight Batches to PRO+
    // tenants only. Backend enforces 402 if a Starter user navigates directly.
    const { covered: hasProTier } = useTierCheck('PRO');
    const [changePasswordOpen, setChangePasswordOpen] = useState(false);
    const [changePasswordLoading, setChangePasswordLoading] = useState(false);
    const [passwordForm] = Form.useForm();

    const handleMenuClick = (info: { key: string }) => {
        navigate(info.key);
        if (isMobile) {
            setDrawerOpen(false);
        }
    };

    const menuItems: ItemType[] = [
        hasPermission('DASHBOARD_VIEW') ? { key: '/', icon: <DashboardOutlined />, label: t('menu.dashboard') } : null,
        hasPermission('PRODUCT_VIEW') ? { key: '/products', icon: <AppstoreOutlined />, label: t('menu.products') } : null,
        hasAnyPermission('ORDER_VIEW', 'ORDER_CREATE') ? {
            key: '/orders-group',
            icon: <FileTextOutlined />,
            label: t('menu.orders'),
            children: [
                hasPermission('ORDER_VIEW') ? { key: '/orders', icon: <UnorderedListOutlined />, label: t('menu.order_list') } : null,
                hasPermission('ORDER_CREATE') ? { key: '/orders/quick', icon: <ThunderboltOutlined />, label: t('menu.quick_order') } : null,
                hasPermission('ORDER_CREATE') ? { key: '/orders/entry', icon: <RocketOutlined />, label: t('menu.order_entry') } : null,
                hasPermission('ORDER_CREATE') ? { key: '/orders/new', icon: <PlusOutlined />, label: t('menu.order_create') } : null,
            ].filter(Boolean),
        } : null,
        hasAnyPermission('INVENTORY_VIEW', 'INVENTORY_MANAGE', 'OUTBOUND_VIEW', 'CHANNEL_VIEW') ? {
            key: '/inventory-group',
            icon: <ShopOutlined />,
            label: t('menu.inventory'),
            children: [
                hasPermission('INVENTORY_VIEW') ? { key: '/inventory', label: t('menu.inventory') } : null,
                hasPermission('INVENTORY_MANAGE') ? { key: '/inventory/scan', icon: <BarcodeOutlined />, label: t('menu.stocktaking') } : null,
                hasPermission('OUTBOUND_VIEW') ? { key: '/inventory/outbound-records', icon: <ExportOutlined />, label: t('menu.outbound_records') } : null,
                hasPermission('OUTBOUND_VIEW') ? { key: '/inventory/inbound-records', icon: <ImportOutlined />, label: t('menu.inbound_records') } : null,
                hasPermission('INVENTORY_VIEW') ? { key: '/stock-takes', icon: <AuditOutlined />, label: t('menu.stock_takes') } : null,
                hasPermission('CHANNEL_VIEW') ? { key: '/channels', icon: <NodeIndexOutlined />, label: t('menu.channels') } : null,
            ].filter(Boolean),
        } : null,
        hasAnyPermission('PURCHASE_ORDER_VIEW', 'PURCHASE_REQUEST_VIEW') ? {
            key: '/procurement-group',
            icon: <ShoppingCartOutlined />,
            label: t('menu.procurement'),
            children: [
                hasPermission('PURCHASE_ORDER_VIEW') ? { key: '/procurement/purchase-orders', icon: <UnorderedListOutlined />, label: t('menu.purchase_orders') } : null,
                hasPermission('PURCHASE_ORDER_MANAGE') ? { key: '/procurement/purchase-orders/new', icon: <PlusOutlined />, label: t('menu.purchase_order_create') } : null,
                hasPermission('PURCHASE_REQUEST_VIEW') ? { key: '/purchase-requests/aggregated', icon: <AppstoreOutlined />, label: t('menu.purchase_requests_aggregated') } : null,
                hasPermission('PURCHASE_REQUEST_VIEW') ? { key: '/purchase-requests', icon: <UnorderedListOutlined />, label: t('menu.purchase_requests') } : null,
            ].filter(Boolean),
        } : null,
        hasAnyPermission('PICK_LIST_VIEW', 'FLIGHT_BATCH_VIEW', 'LOGISTICS_COMPANY_VIEW') ? {
            key: '/logistics-group',
            icon: <RocketOutlined />,
            label: t('menu.logistics'),
            children: [
                hasPermission('PICK_LIST_VIEW') ? { key: '/pick-lists', icon: <UnorderedListOutlined />, label: t('menu.pick_lists') } : null,
                // Flight batches are PRO-only. Hide for Starter tenants — backend
                // returns 402 anyway, but no point dangling unreachable nav.
                hasPermission('FLIGHT_BATCH_VIEW') && hasProTier ? { key: '/flight-batches', icon: <RocketOutlined />, label: t('menu.flight_batches') } : null,
                hasPermission('LOGISTICS_COMPANY_VIEW') ? { key: '/logistics-companies', icon: <RocketOutlined />, label: t('menu.logistics_companies') } : null,
            ].filter(Boolean),
        } : null,
        // Taobao integration is PRO-only. Hide the whole group from Starter.
        hasAnyPermission('TAOBAO_VIEW', 'TAOBAO_MANAGE') && hasProTier ? {
            key: '/taobao-group',
            icon: <ShoppingCartOutlined />,
            label: t('menu.taobao'),
            children: [
                hasPermission('TAOBAO_VIEW') ? { key: '/taobao/orders', icon: <UnorderedListOutlined />, label: t('menu.taobao_orders') } : null,
                hasPermission('TAOBAO_MANAGE') ? { key: '/taobao/settings', icon: <ShopOutlined />, label: t('menu.taobao_settings') } : null,
            ].filter(Boolean),
        } : null,
        hasPermission('CUSTOMER_VIEW') ? { key: '/customers', icon: <TeamOutlined />, label: t('menu.customers') } : null,
        hasPermission('SUPPLIER_VIEW') ? { key: '/suppliers', icon: <HomeOutlined />, label: t('menu.suppliers') } : null,
        hasPermission('WAREHOUSE_VIEW') ? { key: '/warehouses', icon: <ShopOutlined />, label: t('menu.warehouses') } : null,
        hasPermission('USER_VIEW') ? { key: '/users', icon: <TeamOutlined />, label: t('menu.users') } : null,
        hasPermission('ROLE_VIEW') ? { key: '/roles', icon: <SafetyCertificateOutlined />, label: t('menu.roles') } : null,
        hasPermission('AUDIT_VIEW') ? { key: '/audit-logs', icon: <FileSearchOutlined />, label: t('menu.audit_logs') } : null,
        isPlatformAdmin() ? { key: '/tenants', icon: <TeamOutlined />, label: t('menu.tenants') } : null,
        { key: '/settings/subscription', icon: <CrownOutlined />, label: t('menu.subscription') },
        { key: '/settings/tenant', icon: <SettingOutlined />, label: t('menu.tenant_settings') },
    ].filter(Boolean) as ItemType[];

    // Helper to get breadcrumb items based on path
    const getBreadcrumbItems = () => {
        const pathSnippets = location.pathname.split('/').filter(i => i);
        const items = [{ title: t('common.home', 'Home') }];
        pathSnippets.forEach((snippet) => {
            // URL slugs are kebab-case (e.g. "outbound-records") but menu
            // locale keys are snake_case (`menu.outbound_records`). Without
            // this conversion every multi-word route showed the raw slug
            // ("Outbound-records") instead of the translated label.
            const menuKey = `menu.${snippet.replace(/-/g, '_')}`;
            const label = t(menuKey, snippet.charAt(0).toUpperCase() + snippet.slice(1));
            items.push({ title: label });
        });
        return items;
    };

    const displayRole = user?.roles?.[0] || '';

    const handleChangePassword = async (values: { oldPassword: string; newPassword: string }) => {
        setChangePasswordLoading(true);
        try {
            await authApi.changePassword({
                oldPassword: values.oldPassword,
                newPassword: values.newPassword,
            });
            message.success(t('auth.password_changed'));
            setChangePasswordOpen(false);
            passwordForm.resetFields();
        } catch (err: unknown) {
            const error = err as { response?: { data?: { message?: string } } };
            const msg = error?.response?.data?.message || t('common.error');
            message.error(msg);
        } finally {
            setChangePasswordLoading(false);
        }
    };

    const siderContent = (
        <>
            <div className="h-16 flex items-center justify-center p-4 border-b border-gray-700/50">
                <div className={`transition-all duration-300 ${!isMobile && collapsed ? 'scale-75' : 'scale-100'}`}>
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                            <RocketOutlined className="text-white text-lg" />
                        </div>
                        {(isMobile || !collapsed) && (
                            <span className="text-white text-xl font-bold tracking-tight">{t('auth.app_name', '代购助手')}</span>
                        )}
                    </div>
                </div>
            </div>
            {/* Warehouse switcher in the mobile drawer. On desktop the header
                already shows it (in the `hidden md:block` slot), but mobile
                hides that to save header space — so without this, mobile
                users have no way to flip the global warehouse filter. */}
            {isMobile && (
                <div className="px-4 pt-4 pb-2">
                    <WarehouseSwitcher />
                </div>
            )}
            <Menu
                theme="dark"
                mode="inline"
                selectedKeys={[location.pathname]}
                items={menuItems}
                onClick={handleMenuClick}
                style={{ borderRight: 0, marginTop: 16 }}
            />
        </>
    );

    return (
        <Layout className="min-h-screen">
            {isMobile ? (
                <Drawer
                    placement="left"
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    width={260}
                    styles={{ body: { padding: 0, backgroundColor: '#001529' }, header: { display: 'none' } }}
                >
                    {siderContent}
                </Drawer>
            ) : (
                <Sider
                    trigger={null}
                    collapsible
                    collapsed={collapsed}
                    width={260}
                    style={{
                        overflow: 'auto',
                        height: '100vh',
                        position: 'fixed',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        zIndex: 100,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    }}
                >
                    {siderContent}
                </Sider>
            )}
            <Layout style={{ marginLeft: isMobile ? 0 : (collapsed ? 80 : 260), transition: 'all 0.2s' }}>
                <Header
                    style={{
                        padding: isMobile ? '0 12px' : '0 24px',
                        background: darkMode ? 'rgba(20, 20, 20, 0.9)' : 'rgba(255, 255, 255, 0.8)',
                        backdropFilter: 'blur(8px)',
                        position: 'sticky',
                        top: 0,
                        zIndex: 99,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.03)'
                    }}
                >
                    <div className="flex items-center gap-4">
                        {isMobile ? (
                            <Button
                                type="text"
                                icon={<MenuOutlined />}
                                onClick={() => setDrawerOpen(true)}
                                style={{ fontSize: '16px', width: 40, height: 40 }}
                            />
                        ) : (
                            <Button
                                type="text"
                                icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                                onClick={() => setCollapsed(!collapsed)}
                                style={{ fontSize: '16px', width: 40, height: 40 }}
                            />
                        )}
                        {!isMobile && <Breadcrumb items={getBreadcrumbItems()} />}
                    </div>

                    <div className="flex items-center gap-6">
                        <Button
                            type="text"
                            shape="circle"
                            icon={darkMode ? <BulbFilled style={{ color: '#fadb14' }} /> : <BulbOutlined />}
                            onClick={toggleDarkMode}
                            title={darkMode ? t('common.theme_light') : t('common.theme_dark')}
                        />

                        <Dropdown
                            menu={{
                                items: [
                                    { key: 'zh', label: t('common.language_zh'), onClick: () => i18n.changeLanguage('zh') },
                                    { key: 'en', label: t('common.language_en'), onClick: () => i18n.changeLanguage('en') },
                                ],
                                selectedKeys: [i18n.language]
                            }}
                            placement="bottomRight"
                        >
                            <Button type="text" shape="circle" icon={<GlobalOutlined />} />
                        </Dropdown>

                        <div className="hidden md:block">
                            <WarehouseSwitcher />
                        </div>

                        <Input
                            prefix={<SearchOutlined className="text-gray-400" />}
                            placeholder={`${t('common.search')} (Ctrl+K)`}
                            className="w-64 hidden md:flex"
                            variant="filled"
                            style={{ borderRadius: '20px', cursor: 'pointer' }}
                            readOnly
                            onClick={() => document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', ctrlKey: true }))}
                        />

                        <Space size={isMobile ? 'small' : 'middle'}>
                            <Badge count={0} size="small">
                                <Button type="text" shape="circle" icon={<BellOutlined />} />
                            </Badge>
                        </Space>

                        {!isMobile && <div className="h-8 w-px bg-gray-200 mx-2"></div>}

                        <Dropdown menu={{
                            items: [
                                { key: 'profile', label: t('common.profile'), icon: <UserOutlined /> },
                                { key: 'change-password', label: t('auth.change_password'), icon: <LockOutlined />, onClick: () => setChangePasswordOpen(true) },
                                { type: 'divider' },
                                { key: 'logout', label: t('menu.logout'), icon: <LogoutOutlined />, onClick: () => { logout(); navigate('/login'); } }
                            ]
                        }}>
                            <div className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-1 pr-3 rounded-full transition-colors">
                                <Avatar size={isMobile ? 'default' : 'large'} style={{ backgroundColor: '#1677ff' }} icon={<UserOutlined />} />
                                {!isMobile && (
                                    <div className="flex flex-col leading-tight">
                                        <span className="font-medium text-gray-800">{user?.username || t('common.admin')}</span>
                                        <span className="text-xs text-gray-500">{displayRole}</span>
                                    </div>
                                )}
                            </div>
                        </Dropdown>
                    </div>
                </Header>
                <StockTakeLockBanner />
                <Content
                    style={{
                        margin: isMobile ? '12px' : '24px 24px',
                        minHeight: 280,
                    }}
                >
                    <Outlet />
                </Content>
            </Layout>

            <Modal
                title={t('auth.change_password')}
                open={changePasswordOpen}
                onCancel={() => { setChangePasswordOpen(false); passwordForm.resetFields(); }}
                onOk={() => passwordForm.submit()}
                confirmLoading={changePasswordLoading}
                destroyOnClose
            >
                <Form
                    form={passwordForm}
                    layout="vertical"
                    onFinish={handleChangePassword}
                >
                    <Form.Item
                        name="oldPassword"
                        label={t('auth.old_password')}
                        rules={[{ required: true, message: t('auth.password_required') }]}
                    >
                        <Input.Password />
                    </Form.Item>
                    <Form.Item
                        name="newPassword"
                        label={t('auth.new_password')}
                        rules={[
                            { required: true, message: t('auth.password_required') },
                            { min: 6, message: t('auth.password_min_length') },
                        ]}
                    >
                        <Input.Password />
                    </Form.Item>
                    <Form.Item
                        name="confirmPassword"
                        label={t('auth.confirm_password')}
                        dependencies={['newPassword']}
                        rules={[
                            { required: true, message: t('auth.password_required') },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('newPassword') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error(t('auth.password_mismatch')));
                                },
                            }),
                        ]}
                    >
                        <Input.Password />
                    </Form.Item>
                </Form>
            </Modal>

            <CommandPalette />
        </Layout>
    );
};
